import React, { useMemo } from 'react';
import i18n from 'i18n';
import { defaultTo } from 'lodash';
import { SuggestKeywords } from 'core/product/Product';
import { StickableBootstrapTable } from 'components/Table/StickableBootstrapTable';
import { ColumnDefinition, renderColumn, sortableColumn } from 'components/TableColumn/TableColumn';
import { Status } from 'components/Status/Status';
import formatters from 'containers/Limitations/SearchKeywordsComponent/listFormatters';
import {
  COMPETITIVENESS_SORT_ORDER,
  COMPETITIVENESS_STATUS_COLOR,
  KeywordBidCompetitiveness,
  getKeywordBidCompetitiveness
} from 'core/pchome/KeywordBidCompetitiveness';
import styles from './bidEffectivenessCard.module.scss';

export enum BidEffectivenessColumns {
  SUGGEST_KEYWORD = 'suggestKeyword',
  BID_PRICE = 'bidPrice',
  KEYWORD_SEARCH_VOLUME = 'keywordSearchVolume',
  BID_STATUS = 'bidStatus'
}

export const bidStatusFormatter = (competitiveness: KeywordBidCompetitiveness) => (
  <Status
    color={COMPETITIVENESS_STATUS_COLOR[competitiveness]}
    label={i18n.t<string>(`suggestKeywordsList.labels.bidStatuses.${competitiveness}`)}
  />
);

// `bidPrice` 這一欄顯示的是區間，所以標題用 bidPriceRange —— 與 `優化目標` 的兩張表一致。
// 其餘欄名與 i18n key 同名，走機械組出來的那條。
const LABEL_OVERRIDE: { [key: string]: string } = {
  [BidEffectivenessColumns.BID_PRICE]: 'suggestKeywordsList.labels.bidPriceRange'
};

// 可排序的欄位與 `優化目標` 的兩張關鍵字表一致：成效欄可排，關鍵字本身不排。
const columnDefinition = (columnName: string, sortable: boolean = false): ColumnDefinition => ({
  ...sortableColumn(
    columnName,
    defaultTo(LABEL_OVERRIDE[columnName], `suggestKeywordsList.labels.${columnName}`),
    sortable
  ),
  classes: () => styles[columnName],
  headerClasses: () => styles[columnName]
});

export type BidEffectivenessCardProps = {
  /** 已選關鍵字的成效列。只有關鍵字型活動有，故可能是 undefined。 */
  selectedKeywordRows?: SuggestKeywords[];
  /** `基本資訊` 的「出價上限」（formik `bidPrice`）。改它時整欄狀況要跟著重算。 */
  bidCap?: number;
  /**
   * 經銷商出價下限（`getBidPriceRange(optimize).min`）。判定與顯示的區間都會被它抬高,
   * 所以它必填 —— 漏傳就會讓卡片與左側清單各說一套。父層已有 `bidPriceRange`,
   * 直接取 `.min`,不要另外算一份。
   */
  bidFloor: number;
};

/**
 * `基本資訊` step 右側的「出價效益參考」卡片：標題 + 一張四欄表。
 * 零新 API —— 四欄全部由手上的 `selectedKeywordRows` 與 formik 的 `bidPrice` 推導。
 */
export const BidEffectivenessCard: React.FC<BidEffectivenessCardProps> = ({
  selectedKeywordRows,
  bidCap,
  bidFloor
}) => {
  // 出價狀況算好寫進 data，而不是留在 formatter 裡從閉包讀 bidCap。兩個理由：一是底層
  // react-bootstrap-table-next 的 Cell 只認 row 與 formatExtraData 的變動，換掉 formatter
  // 的函式識別不會讓 cell 重畫（會靜默停在舊狀態）；二是這一欄要可排序，而排序讀的是 data
  // —— 值不在 row 裡就排不動。
  //
  // deps 要涵蓋每一個會進到 row 裡的值。少一個，cell 就會停在舊狀態而且不報錯。
  const rows = useMemo(
    () => (selectedKeywordRows ? selectedKeywordRows : []).map(row => ({
      ...row,
      [BidEffectivenessColumns.BID_STATUS]: getKeywordBidCompetitiveness(bidCap, row, bidFloor)
    })),
    [selectedKeywordRows, bidCap, bidFloor]
  );

  const columns = useMemo(() => [
    renderColumn(columnDefinition(BidEffectivenessColumns.SUGGEST_KEYWORD)),
    renderColumn(
      columnDefinition(BidEffectivenessColumns.BID_PRICE, true),
      // 不需要像左側清單那樣把 bidFloor 放進 formatExtraData：bidFloor 在上面 rows 的 deps
      // 裡，它一變 rows 就是新物件，而 Cell 認 row 的變動 ⇒ 已經會重畫。
      (bidPrice: number, row: SuggestKeywords) =>
        formatters.bidRangeFormatter(bidPrice, row, bidFloor)
    ),
    renderColumn(
      columnDefinition(BidEffectivenessColumns.KEYWORD_SEARCH_VOLUME, true),
      formatters.keywordSearchVolumeFormatter
    ),
    renderColumn(
      {
        ...columnDefinition(BidEffectivenessColumns.BID_STATUS, true),
        // cell 是 KeywordBidCompetitiveness 的字串值，直接排會變成按字母 ⇒ 換成名次。
        sortValue: (cell: KeywordBidCompetitiveness) => COMPETITIVENESS_SORT_ORDER[cell]
      },
      bidStatusFormatter
    )
  ], [bidFloor]);

  return (
    <div className={styles.bidEffectivenessCard}>
      <div className={styles.title}>
        {i18n.t<string>('campaignInfo.labels.bidEffectivenessTitle')}
      </div>
      <StickableBootstrapTable
        keyField={BidEffectivenessColumns.SUGGEST_KEYWORD}
        data={rows}
        columns={columns}
        noDataIndication={'campaignInfo.labels.bidEffectivenessEmpty'}
        scrollByElementControl={false}
        customPadding={true}
        hidePagination
      />
    </div>
  );
};

export default BidEffectivenessCard;
